import { useId, useState, type KeyboardEvent } from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { graduations, useLargeur, type SerieGraphe } from './GrapheColonnes.js'
import { couleursCategories } from './palette.js'

/**
 * Courbe en AIRE du tableau de bord — l'évolution qu'on lit d'abord.
 *
 * ⭐ La première série est l'aire (le chiffre d'affaires), la seconde une
 * ligne sur le MÊME axe (le bénéfice net, en francs lui aussi). Jamais de
 * second axe : deux échelles superposées se lisent l'une contre l'autre alors
 * qu'elles ne se comparent pas.
 *
 * ⭐ Courbe LISSÉE, mais monotone (Fritsch-Carlson) : une courbe de Bézier
 * ordinaire dépasse ses points, et dessinerait un creux sous zéro entre deux
 * jours à zéro — une vente négative qui n'a jamais eu lieu.
 */

const MARGE_HAUT = 16
const MARGE_BAS = 26
const MARGE_DROITE = 12

/** Tracé lissé et MONOTONE : il ne dépasse jamais les points qu'il relie. */
function cheminLisse(points: readonly { x: number; y: number }[]): string {
  const n = points.length
  if (n === 0) return ''
  if (n === 1) return `M${points[0]!.x},${points[0]!.y}`
  const dx: number[] = []
  const pente: number[] = []
  for (let i = 0; i < n - 1; i++) {
    const a = points[i]!
    const b = points[i + 1]!
    dx.push(b.x - a.x)
    pente.push((b.y - a.y) / (b.x - a.x || 1))
  }
  const t: number[] = [pente[0]!]
  for (let i = 1; i < n - 1; i++) {
    const p0 = pente[i - 1]!
    const p1 = pente[i]!
    // Changement de sens, ou palier : tangente nulle — c'est ce qui empêche
    // la courbe de dépasser.
    t.push(
      p0 * p1 <= 0
        ? 0
        : (3 * (dx[i - 1]! + dx[i]!)) /
            ((2 * dx[i]! + dx[i - 1]!) / p0 + (dx[i]! + 2 * dx[i - 1]!) / p1),
    )
  }
  t.push(pente[n - 2]!)
  let d = `M${points[0]!.x},${points[0]!.y}`
  for (let i = 0; i < n - 1; i++) {
    const a = points[i]!
    const b = points[i + 1]!
    const h = dx[i]! / 3
    d += ` C${a.x + h},${a.y + h * t[i]!} ${b.x - h},${b.y - h * t[i + 1]!} ${b.x},${b.y}`
  }
  return d
}

export function GrapheAire({
  titre,
  libelles,
  libellesLongs,
  aire,
  ligne,
  formater,
  formaterAxe,
  hauteur = 260,
  sansLegende = false,
}: {
  titre: string
  libelles: readonly string[]
  libellesLongs: readonly string[]
  /** La série tracée en aire. */
  aire: SerieGraphe
  /** Une seconde série, en ligne, sur le même axe. */
  ligne?: SerieGraphe
  formater: (v: number) => string
  formaterAxe: (v: number) => string
  hauteur?: number
  /** Masque la légende quand la carte porte déjà les pastilles, chiffrées. */
  sansLegende?: boolean
}): JSX.Element {
  const theme = useTheme()
  const sombre = theme.palette.mode === 'dark'
  const [c0, c1] = couleursCategories(sombre) as [string, string]
  const surface = theme.palette.background.paper
  const degrade = useId().replace(/:/g, '')
  const [ref, largeur] = useLargeur()
  const [actif, setActif] = useState<number | null>(null)

  const n = libelles.length
  const series = [aire, ...(ligne ? [ligne] : [])]
  const couleurDe = (k: number): string => (k === 0 ? c0 : c1)
  const toutes = series.flatMap((s) => s.valeurs.filter((v): v is number => v !== null))
  const vide = toutes.every((v) => v === 0)

  let min = Math.min(0, ...toutes)
  let max = Math.max(0, ...toutes)
  if (max === min) max = min + 1000
  const ticks = graduations(min, max, false)
  min = ticks[0] ?? min
  max = ticks.at(-1) ?? max

  const margeGauche = Math.max(...ticks.map((t) => formaterAxe(t).length)) * 7 + 12
  const plotW = Math.max(0, largeur - margeGauche - MARGE_DROITE)
  const plotH = hauteur - MARGE_HAUT - MARGE_BAS
  const x = (i: number): number => margeGauche + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW)
  const y = (v: number): number => MARGE_HAUT + ((max - v) / (max - min)) * plotH

  /** Les tronçons continus d'une série — un `null` coupe le tracé. */
  const troncons = (s: SerieGraphe): { x: number; y: number }[][] => {
    const liste: { x: number; y: number }[][] = []
    let courant: { x: number; y: number }[] = []
    s.valeurs.forEach((v, i) => {
      if (v === null) {
        if (courant.length) liste.push(courant)
        courant = []
      } else courant.push({ x: x(i), y: y(v) })
    })
    if (courant.length) liste.push(courant)
    return liste
  }

  // Étiquettes sélectives, comptées depuis la DERNIÈRE : la période en cours
  // est toujours nommée.
  const pasEtiquette = Math.max(1, Math.ceil(n / Math.max(1, Math.floor(plotW / 56))))

  function surTouche(e: KeyboardEvent): void {
    if (n === 0) return
    const courant = actif ?? n - 1
    const cible =
      e.key === 'ArrowLeft'
        ? Math.max(0, courant - 1)
        : e.key === 'ArrowRight'
          ? Math.min(n - 1, courant + 1)
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? n - 1
              : null
    if (e.key === 'Escape') setActif(null)
    if (cible === null) return
    e.preventDefault()
    setActif(cible)
  }

  /** Le réticule trouve l'abscisse : on vise une date, pas un trait de 2 px. */
  function surPointeur(clientX: number, gauche: number): void {
    if (n === 0 || plotW <= 0) return
    const rel = (clientX - gauche - margeGauche) / plotW
    setActif(Math.max(0, Math.min(n - 1, Math.round(rel * (n - 1)))))
  }

  return (
    <Box>
      {ligne && !vide && !sansLegende ? (
        // ⭐ Légende dès deux séries : l'identité ne repose jamais sur la seule
        // couleur. La pastille reprend la marque — carré pour l'aire, trait
        // pour la ligne.
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
          {series.map((s, k) => (
            <Box key={s.nom} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {k === 0 ? (
                <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: couleurDe(k) }} />
              ) : (
                <Box sx={{ width: 14, height: 2, borderRadius: 1, bgcolor: couleurDe(k) }} />
              )}
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {s.nom}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}

      <Box
        ref={ref}
        tabIndex={0}
        role="group"
        aria-label={`${titre}. Flèches gauche et droite pour parcourir les périodes.`}
        onKeyDown={surTouche}
        onFocus={() => setActif((a) => a ?? n - 1)}
        onBlur={() => setActif(null)}
        sx={{
          position: 'relative',
          height: hauteur,
          outline: 'none',
          borderRadius: 1,
          '&:focus-visible': { boxShadow: `0 0 0 2px ${theme.palette.primary.main}` },
        }}
      >
        {vide ? (
          <Box
            sx={{ height: '100%', display: 'grid', placeItems: 'center', color: 'text.secondary' }}
          >
            <Typography variant="body2">Aucune vente sur la période.</Typography>
          </Box>
        ) : largeur > 0 ? (
          <svg
            width={largeur}
            height={hauteur}
            role="img"
            aria-label={titre}
            onPointerMove={(e) =>
              surPointeur(e.clientX, e.currentTarget.getBoundingClientRect().left)
            }
            onPointerDown={(e) =>
              surPointeur(e.clientX, e.currentTarget.getBoundingClientRect().left)
            }
            onPointerLeave={() => setActif(null)}
            style={{ display: 'block', overflow: 'visible', touchAction: 'pan-y' }}
          >
            <defs>
              <linearGradient id={degrade} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={c0} stopOpacity={0.18} />
                <stop offset="100%" stopColor={c0} stopOpacity={0.02} />
              </linearGradient>
            </defs>

            {/* Grille : filets de 1 px, pleins, en retrait. */}
            {ticks.map((t) => (
              <g key={t}>
                <line
                  x1={margeGauche}
                  x2={largeur - MARGE_DROITE}
                  y1={y(t)}
                  y2={y(t)}
                  stroke={t === 0 && min < 0 ? theme.palette.text.disabled : theme.palette.divider}
                  strokeWidth={1}
                  shapeRendering="crispEdges"
                />
                <text
                  x={margeGauche - 8}
                  y={y(t)}
                  dy="0.35em"
                  textAnchor="end"
                  fontSize={11}
                  fill={theme.palette.text.secondary}
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {formaterAxe(t)}
                </text>
              </g>
            ))}

            {/* L'aire : un voile dégradé sous la courbe, jamais un bloc plein. */}
            {troncons(aire).map((t, k) => {
              const courbe = cheminLisse(t)
              const base = y(Math.max(min, 0))
              return (
                <g key={k}>
                  <path
                    d={`${courbe} L${t.at(-1)!.x},${base} L${t[0]!.x},${base} Z`}
                    fill={`url(#${degrade})`}
                  />
                  <path d={courbe} fill="none" stroke={c0} strokeWidth={2} strokeLinecap="round" />
                </g>
              )
            })}

            {ligne
              ? troncons(ligne).map((t, k) => (
                  <path
                    key={k}
                    d={cheminLisse(t)}
                    fill="none"
                    stroke={c1}
                    strokeWidth={2}
                    strokeLinecap="round"
                  />
                ))
              : null}

            {/* Réticule : un filet vertical sur la période visée. */}
            {actif !== null ? (
              <line
                x1={x(actif)}
                x2={x(actif)}
                y1={MARGE_HAUT}
                y2={MARGE_HAUT + plotH}
                stroke={theme.palette.text.disabled}
                strokeWidth={1}
                shapeRendering="crispEdges"
              />
            ) : null}

            {/* Points : sur la période visée, sinon au bout de chaque série.
                Cerclés de surface pour rester lisibles là où ils se croisent. */}
            {series.map((s, k) => {
              const i = actif ?? [...s.valeurs.keys()].reverse().find((j) => s.valeurs[j] !== null)
              const v = i === undefined ? null : s.valeurs[i]
              return i === undefined || v === null || v === undefined ? null : (
                <circle
                  key={s.nom}
                  cx={x(i)}
                  cy={y(v)}
                  r={4.5}
                  fill={couleurDe(k)}
                  stroke={surface}
                  strokeWidth={2}
                />
              )
            })}

            {libelles.map((l, i) =>
              (n - 1 - i) % pasEtiquette === 0 ? (
                <text
                  key={i}
                  x={x(i)}
                  y={hauteur - 8}
                  textAnchor={i === 0 && n > 1 ? 'start' : i === n - 1 && n > 1 ? 'end' : 'middle'}
                  fontSize={11}
                  fontWeight={i === n - 1 ? 700 : 400}
                  fill={i === n - 1 ? theme.palette.text.primary : theme.palette.text.secondary}
                >
                  {l}
                </text>
              ) : null,
            )}
          </svg>
        ) : null}

        {actif !== null && !vide ? (
          <Box
            role="status"
            aria-live="polite"
            sx={{
              position: 'absolute',
              top: MARGE_HAUT,
              ...(x(actif) > largeur / 2
                ? { right: largeur - x(actif) + 10 }
                : { left: x(actif) + 10 }),
              whiteSpace: 'nowrap',
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              boxShadow: 3,
              px: 2,
              py: 1.5,
              minWidth: 170,
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
              {libellesLongs[actif]}
            </Typography>
            {/* Une infobulle, TOUTES les séries : la valeur d'abord, forte ; le
                nom ensuite, en retrait. */}
            {series.map((s, k) => {
              const v = s.valeurs[actif]
              return (
                <Box
                  key={s.nom}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 2, borderRadius: 1, bgcolor: couleurDe(k) }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {s.nom}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {v === null || v === undefined ? '—' : formater(v)}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}
